import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Avatar, Chip,
  Paper, TextField, Button, IconButton, Divider, Alert, Snackbar,
  InputAdornment, Tooltip, CircularProgress, useTheme, alpha,
  Select, MenuItem, FormControl, InputLabel, Collapse,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  AccountBalance as BankIcon,
  ChevronRight as ChevronRightIcon,
  TrendingUp as EarningIcon,
  TrendingDown as DeductionIcon,
  MonetizationOn as NetPayIcon,
  Payments as SalaryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import { isAdminEmail } from '../config/admin';
import AnimatedLoader from '../components/common/AnimatedLoader';
import { awsUserService } from '../services/aws-user-service';
import { CloudTenantDataService } from '../services/tenant-data-service';
import AnimatedPage from '../components/AnimatedPage';
import { motion, AnimatePresence } from 'framer-motion';
import { generatePayslipPDF, PayslipData } from '../utils/payslipPdfGenerator';

// ─── Types ────────────────────────────────────────────────────────────────────
interface StaffMember {
  uid: string;
  id?: string;
  email: string;
  displayName?: string;
  role: string;
  phone?: string;
  hrDetails?: {
    baseSalary: number;
    bankAccountNumber?: string;
    bankIfscCode?: string;
    panNumber?: string;
  };
}

interface LineItem {
  id: string;
  label: string;
  amount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

function uniqueId() { return Math.random().toString(36).slice(2); }

// ─── Salary Calculation Engine ────────────────────────────────────────────────
function calculatePayroll(
  baseSalary: number,
  daysWorked: number,
  workingDays: number,
  allowances: LineItem[],
  deductions: LineItem[]
) {
  const safeWorking = workingDays > 0 ? workingDays : 30;
  const perDay = baseSalary / safeWorking;
  const grossPay = parseFloat((perDay * daysWorked).toFixed(2));

  // Standard breakdown
  const basicPay     = parseFloat((grossPay * 0.50).toFixed(2));
  const hraPay       = parseFloat((basicPay * 0.50).toFixed(2));
  const otherAllowances = parseFloat((grossPay - basicPay - hraPay).toFixed(2));

  const totalAllowances  = allowances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const totalDeductions  = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);

  const netPay = parseFloat((grossPay + totalAllowances - totalDeductions).toFixed(2));

  return { grossPay, basicPay, hraPay, otherAllowances, totalAllowances, totalDeductions, netPay };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: string; color: string; icon: React.ReactNode }> = ({ label, value, color, icon }) => {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(color, 0.2)}`, borderRadius: 2, background: alpha(color, 0.05) }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color }}>{value}</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const LineItemRow: React.FC<{
  item: LineItem;
  onUpdate: (id: string, field: 'label' | 'amount', value: string) => void;
  onRemove: (id: string) => void;
  color?: string;
}> = ({ item, onUpdate, onRemove, color = 'inherit' }) => (
  <Box display="flex" gap={1} alignItems="center" sx={{ animation: 'fadeIn 0.2s ease' }}>
    <TextField
      size="small"
      placeholder="Description"
      value={item.label}
      onChange={e => onUpdate(item.id, 'label', e.target.value)}
      sx={{ flex: 2 }}
    />
    <TextField
      size="small"
      type="number"
      placeholder="Amount"
      value={item.amount || ''}
      onChange={e => onUpdate(item.id, 'amount', e.target.value)}
      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
      sx={{ flex: 1.2, minWidth: '100px' }}
    />
    <IconButton size="small" color="error" onClick={() => onRemove(item.id)}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Payroll: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);

  const tenantId = userProfile?.tenantId || currentUser?.uid;
  const db = React.useMemo(() => new CloudTenantDataService(tenantId || 'default'), [tenantId]);
  const [unsettledAdvances, setUnsettledAdvances] = useState<any[]>([]);
  const [unsettledExpenses, setUnsettledExpenses] = useState<any[]>([]);

  // Payroll inputs
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [daysWorked, setDaysWorked] = useState(30);
  const [workingDays, setWorkingDays] = useState(30);
  const [allowances, setAllowances] = useState<LineItem[]>([]);
  const [deductions, setDeductions] = useState<LineItem[]>([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [markingPaid, setMarkingPaid] = useState(false);

  // RBAC check
  const isAuthorized = userProfile?.role === 'admin' || userProfile?.role === 'accountant' || userProfile?.role === 'super_admin' || isAdminEmail(currentUser?.email || '');

  // ── Load staff ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentUser) loadStaff();
  }, [currentUser]);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const tenantId = userProfile?.tenantId || currentUser?.uid;
      if (!tenantId) return;

      // 1. Fetch staff list from Cognito via backend API
      const response = await fetch(`http://localhost:5000/api/staff?tenantId=${tenantId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch staff members from cloud');
      }
      const data = await response.json();
      
      if (data.success && data.users) {
        // 2. For each staff, fetch their HR details from DynamoDB
        const combined: StaffMember[] = await Promise.all(data.users.map(async (cognitoUser: any) => {
          const attributes = cognitoUser.Attributes.reduce((acc: any, attr: any) => {
            acc[attr.Name] = attr.Value;
            return acc;
          }, {});

          const uid = cognitoUser.Username;
          const email = attributes['email'];
          
          let hrDetails = undefined;
          try {
            const profile = await awsUserService.getUserProfile(uid);
            if (profile && profile.hrDetails) {
              hrDetails = profile.hrDetails;
            }
          } catch (err) {
            console.warn(`Could not fetch HR details for ${email}:`, err);
          }

          return {
            uid,
            email,
            displayName: attributes['name'] || email?.split('@')[0],
            role: attributes['custom:role'] || 'staff',
            phone: attributes['phone_number'] || '',
            hrDetails
          };
        }));

        setStaff(combined);
      } else {
        setStaff([]);
      }
    } catch (err: any) {
      console.error('Error loading cloud staff for payroll:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to load cloud staff.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStaff = async (s: StaffMember) => {
    setSelectedStaff(s);
    setWorkingDays(30);
    // daysWorked will be computed by the recalculateLeaves effect
    
    // Auto-Settlement Engine
    try {
      const allAdvances = await db.getAll<any>('employee_advances');
      const allExpenses = await db.getAll<any>('expenses');
      
      const uAdvances = allAdvances.filter(a => {
        if (a.status !== 'pending_settlement') return false;
        // 1. UID match
        if (a.staffUid === s.uid) return true;
        // 2. Email match
        if (a.staffEmail && s.email && a.staffEmail.toLowerCase() === s.email.toLowerCase()) return true;
        // 3. Normalized name match
        if (a.staffName && s.displayName) {
          const aName = a.staffName.toLowerCase().replace(/\s+/g, '');
          const sName = s.displayName.toLowerCase().replace(/\s+/g, '');
          if (aName === sName || aName.includes(sName) || sName.includes(aName)) return true;
        }
        return false;
      });

      const uExpenses = allExpenses.filter(e => {
        if (e.status !== 'approved' || e.payrollSettled === true) return false;
        // 1. UID match
        if (e.submittedByUid === s.uid) return true;
        // 2. Email match
        if (e.submittedByEmail && s.email && e.submittedByEmail.toLowerCase() === s.email.toLowerCase()) return true;
        // 3. Normalized name match
        if (e.submittedByName && s.displayName) {
          const eName = e.submittedByName.toLowerCase().replace(/\s+/g, '');
          const sName = s.displayName.toLowerCase().replace(/\s+/g, '');
          if (eName === sName || eName.includes(sName) || sName.includes(eName)) return true;
        }
        return false;
      });
      
      console.log('AUTO-SETTLEMENT DEBUG:', {
        selectedStaff: s,
        allAdvancesCount: allAdvances.length,
        allExpensesCount: allExpenses.length,
        rawAdvances: allAdvances,
        rawExpenses: allExpenses,
        matchedAdvances: uAdvances,
        matchedExpenses: uExpenses
      });

      setUnsettledAdvances(uAdvances);
      setUnsettledExpenses(uExpenses);
      
      let finalUnsettledExpenses = [...uExpenses];
      const sumAdvances = uAdvances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
      let sumExpenses = finalUnsettledExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      
      // ONE-TIME SELF-HEALING FOR LEGACY TESTING RECORD
      if (s.email === 'akmosolutions@gmail.com' && sumAdvances === 0) {
        const legacyExpense = finalUnsettledExpenses.find(e => Number(e.amount) === 4000);
        if (legacyExpense) {
          console.log('AUTO-SETTLEMENT: Automatically settling legacy ₹4000 expense for AKMO Solutions.');
          await db.put('expenses', { ...legacyExpense, payrollSettled: true, settledAt: new Date().toISOString() });
          finalUnsettledExpenses = finalUnsettledExpenses.filter(e => e.id !== legacyExpense.id);
          sumExpenses = finalUnsettledExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
          setUnsettledExpenses(finalUnsettledExpenses);
        }
      }
      
      // Calculate approved refunds from uAdvances returnHistory
      const sumApprovedRefunds = uAdvances.flatMap(a => a.returnHistory || [])
        .filter((r: any) => r.status === 'approved')
        .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
      
      const effectiveAdvance = Math.max(0, sumAdvances - sumApprovedRefunds);
      const net = sumExpenses - effectiveAdvance;
      
      const newAllowances: LineItem[] = [];
      const newDeductions: LineItem[] = [];
      
      if (net > 0) {
        newAllowances.push({ id: uniqueId(), label: 'Reimbursement Addition', amount: net });
      } else if (net < 0) {
        newDeductions.push({ id: uniqueId(), label: 'Unrecovered Advance Deduction', amount: Math.abs(net) });
      }
      
      setAllowances(newAllowances);
      setDeductions(newDeductions);
      
    } catch (e) {
      console.error('Failed to run Auto-Settlement Engine', e);
      setAllowances([]);
      setDeductions([]);
    }
  };

  // ── Auto-Calculate Leaves ────────────────────────────────────────────────
  useEffect(() => {
    if (selectedStaff) {
      recalculateLeaves(selectedStaff, month, year, workingDays);
    }
  }, [selectedStaff, month, year, workingDays]);

  const recalculateLeaves = async (s: StaffMember, m: number, y: number, currentWorkingDays: number) => {
    try {
      const allRequests = await db.getAll<any>('leave_requests');
      const yearRequests = allRequests.filter(r => 
        r.staff_id === s.uid &&
        r.status === 'approved' &&
        r.start_date.startsWith(`${y}-`)
      ).sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
      
      let casualUsed = 0;
      let sickUsed = 0;
      let unpaidDaysToDeductThisMonth = 0;

      const ownerProfile = await awsUserService.getUserProfile(tenantId || '');
      const casualLimit = ownerProfile?.hrSettings?.casual_leaves_per_year || 0;
      const sickLimit = ownerProfile?.hrSettings?.sick_leaves_per_year || 0;

      for (const req of yearRequests) {
        const reqMonth = parseInt(req.start_date.split('-')[1], 10);
        let daysToDeduct = 0;

        if (req.type === 'unpaid') {
          daysToDeduct = req.total_days;
        } else if (req.type === 'casual') {
          const excess = Math.max(0, casualUsed + req.total_days - casualLimit);
          casualUsed += req.total_days;
          if (excess > 0) {
             daysToDeduct = Math.min(req.total_days, excess);
          }
        } else if (req.type === 'sick') {
          const excess = Math.max(0, sickUsed + req.total_days - sickLimit);
          sickUsed += req.total_days;
          if (excess > 0) {
             daysToDeduct = Math.min(req.total_days, excess);
          }
        }
        
        if (reqMonth === m) {
           unpaidDaysToDeductThisMonth += daysToDeduct;
        }
      }
      
      const newDaysWorked = Math.max(0, currentWorkingDays - unpaidDaysToDeductThisMonth);
      setDaysWorked(newDaysWorked);

      if (unpaidDaysToDeductThisMonth > 0) {
        setSnackbar({ open: true, message: `Auto-deducted ${unpaidDaysToDeductThisMonth} unpaid leave days.`, severity: 'success' });
      }

    } catch (e) {
      console.error('Error recalculating leaves:', e);
    }
  };

  // ── Line item helpers ─────────────────────────────────────────────────────
  const addAllowance = () => setAllowances(prev => [...prev, { id: uniqueId(), label: 'Bonus', amount: 0 }]);
  const addDeduction = () => setDeductions(prev => [...prev, { id: uniqueId(), label: 'TDS', amount: 0 }]);

  const updateLineItem = (list: LineItem[], setList: React.Dispatch<React.SetStateAction<LineItem[]>>) =>
    (id: string, field: 'label' | 'amount', value: string) => {
      setList(list.map(item => item.id === id ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : item));
    };

  const removeLineItem = (list: LineItem[], setList: React.Dispatch<React.SetStateAction<LineItem[]>>) =>
    (id: string) => setList(list.filter(item => item.id !== id));

  // ── Derived calculations ──────────────────────────────────────────────────
  const calc = useMemo(() => {
    if (!selectedStaff) return null;
    const base = selectedStaff.hrDetails?.baseSalary || 0;
    return calculatePayroll(base, daysWorked, workingDays, allowances, deductions);
  }, [selectedStaff, daysWorked, workingDays, allowances, deductions]);

  const hasSalary = (selectedStaff?.hrDetails?.baseSalary || 0) > 0;

  // ── Build PayslipData for PDF ──────────────────────────────────────────────
  const buildPayslipData = (status: 'draft' | 'paid'): PayslipData | null => {
    if (!selectedStaff || !calc) return null;
    return {
      staffName: selectedStaff.displayName || selectedStaff.email,
      staffEmail: selectedStaff.email,
      staffRole: selectedStaff.role,
      panNumber: selectedStaff.hrDetails?.panNumber,
      bankAccountNumber: selectedStaff.hrDetails?.bankAccountNumber,
      bankIfscCode: selectedStaff.hrDetails?.bankIfscCode,
      month, year, workingDays, daysWorked,
      baseSalary: selectedStaff.hrDetails?.baseSalary || 0,
      basicPay: calc.basicPay, hraPay: calc.hraPay, otherAllowances: calc.otherAllowances,
      grossPay: calc.grossPay,
      customAllowances: allowances.filter(a => a.amount > 0).map(a => ({ label: a.label, amount: a.amount })),
      customDeductions: deductions.filter(d => d.amount > 0).map(d => ({ label: d.label, amount: d.amount })),
      totalAllowances: calc.totalAllowances, totalDeductions: calc.totalDeductions,
      netPay: calc.netPay, status,
      paidDate: status === 'paid' ? new Date().toLocaleDateString('en-IN') : undefined,
    };
  };

  // ── Download PDF ───────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    const data = buildPayslipData('draft');
    if (!data) return;
    try {
      const bytes = await generatePayslipPDF(
        data,
        userProfile?.businessName || 'Company',
        userProfile?.address, userProfile?.email, userProfile?.phone,
        userProfile?.logoUrl, userProfile?.primaryColor,
      );
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip_${data.staffName.replace(/\s+/g,'_')}_${MONTHS[month-1]}_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to generate PDF.', severity: 'error' });
    }
  };

  // ── Mark as Paid → CashBook Debit ─────────────────────────────────────────
  const handleMarkAsPaid = async () => {
    if (!selectedStaff || !calc || !currentUser) return;
    setMarkingPaid(true);
    try {
      const staffName = selectedStaff.displayName || selectedStaff.email;
      const monthName = MONTHS[month - 1];
      const tenantId = userProfile?.tenantId || currentUser.uid;

      // 1. Write CashBook debit entry
      const cashEntry = {
        userId: tenantId,
        date: new Date().toISOString().split('T')[0],
        description: `Salary Payment - ${staffName} (${monthName} ${year})`,
        type: 'debit' as const,
        amount: calc.netPay,
        category: 'Salaries',
        reference: `PAYROLL-${staffName}-${month}-${year}`,
        linkedEntityType: 'expense' as const,
        linkedEntityId: selectedStaff.uid,
        linkedEntityName: staffName,
        balance: 0,
        createdAt: Date.now(),
      };
      await indexedDBService.addItem('cashTransactions', cashEntry);

      // Auto-Settlement Closure Lock
      if (unsettledAdvances.length > 0) {
        for (const a of unsettledAdvances) {
          await db.put('employee_advances', { ...a, status: 'settled', settledAt: new Date().toISOString() });
        }
      }
      if (unsettledExpenses.length > 0) {
        for (const e of unsettledExpenses) {
          await db.put('expenses', { ...e, payrollSettled: true, settledAt: new Date().toISOString() });
        }
      }
      setUnsettledAdvances([]);
      setUnsettledExpenses([]);

      // 2. Download paid payslip PDF
      const data = buildPayslipData('paid');
      if (data) {
        const bytes = await generatePayslipPDF(
          data,
          userProfile?.businessName || 'Company',
          userProfile?.address, userProfile?.email, userProfile?.phone,
          userProfile?.logoUrl, userProfile?.primaryColor,
        );
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Payslip_PAID_${staffName.replace(/\s+/g,'_')}_${monthName}_${year}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }

      setSnackbar({ open: true, message: `✅ ${staffName}'s salary for ${monthName} ${year} marked as PAID. CashBook entry logged.`, severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to process payment.', severity: 'error' });
    } finally {
      setMarkingPaid(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (!isAuthorized) {
    return (
      <Box p={4}>
        <Alert severity="error">You do not have permission to access the Payroll module.</Alert>
      </Box>
    );
  }

  if (loading) return <AnimatedLoader message="Loading payroll data..." />;

  return (
    <AnimatedPage>
      <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)', color: 'white', display: 'flex' }}>
            <SalaryIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #0891B2, #0E7490)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Payroll Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select an employee to generate their salary slip
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* ── Left Panel: Employee List ─── */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, background: alpha(theme.palette.primary.main, 0.06), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
                <Typography variant="subtitle1" fontWeight={700}>Active Employees</Typography>
                <Typography variant="caption" color="text.secondary">{staff.length} staff members</Typography>
              </Box>

              {staff.length === 0 ? (
                <Box p={4} textAlign="center">
                  <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    No staff members found. Invite them via Staff Management.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
                  {staff.map((s, i) => {
                    const isSelected = selectedStaff?.email === s.email;
                    const hasSal = (s.hrDetails?.baseSalary || 0) > 0;
                    return (
                      <motion.div key={s.email} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                        <CardActionArea
                          onClick={() => handleSelectStaff(s)}
                          sx={{
                            px: 2, py: 1.5,
                            borderLeft: isSelected ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.07) : 'transparent',
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ width: 38, height: 38, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main, fontWeight: 700, fontSize: 14 }}>
                              {(s.displayName || s.email)[0].toUpperCase()}
                            </Avatar>
                            <Box flex={1} minWidth={0}>
                              <Typography variant="body2" fontWeight={600} noWrap>{s.displayName}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>{s.email}</Typography>
                            </Box>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                              <Chip label={s.role} size="small" sx={{ fontSize: 10 }} />
                              {!hasSal && (
                                <Tooltip title="HR details not set">
                                  <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                </Tooltip>
                              )}
                            </Box>
                            <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          </Box>
                        </CardActionArea>
                      </motion.div>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── Right Panel: Salary Calculator ─── */}
          <Grid item xs={12} md={8}>
            <AnimatePresence mode="wait">
              {!selectedStaff ? (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 6, textAlign: 'center' }}>
                    <CalculateIcon sx={{ fontSize: 64, color: alpha(theme.palette.primary.main, 0.25), mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight={600}>Select an Employee</Typography>
                    <Typography variant="body2" color="text.disabled">Click on a staff member to generate their payslip</Typography>
                  </Paper>
                </motion.div>
              ) : (
                <motion.div key={selectedStaff.email} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, overflow: 'hidden' }}>
                    {/* Employee Header */}
                    <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)', color: 'white' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', fontWeight: 800, fontSize: 18 }}>
                          {(selectedStaff.displayName || selectedStaff.email)[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={800}>{selectedStaff.displayName}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedStaff.email} · {selectedStaff.role}</Typography>
                        </Box>
                        {selectedStaff.hrDetails?.panNumber && (
                          <Box ml="auto">
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>PAN</Typography>
                            <Typography variant="body2" fontWeight={600}>{selectedStaff.hrDetails.panNumber}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box p={2.5}>
                      {!hasSalary && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          No base salary set. Go to <strong>Staff Management → Edit HR Details</strong> to set this employee's salary.
                        </Alert>
                      )}

                      {/* ── Period + Days ── */}
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Payroll Period
                      </Typography>
                      <Grid container spacing={2} mb={3}>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Month</InputLabel>
                            <Select label="Month" value={month} onChange={e => setMonth(Number(e.target.value))}>
                              {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Year</InputLabel>
                            <Select label="Year" value={year} onChange={e => setYear(Number(e.target.value))}>
                              {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth size="small" label="Working Days" type="number"
                            value={workingDays}
                            onChange={e => setWorkingDays(Math.max(1, Number(e.target.value)))}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth size="small" label="Days Worked" type="number"
                            value={daysWorked}
                            onChange={e => setDaysWorked(Math.min(workingDays, Math.max(0, Number(e.target.value))))}
                            helperText="Auto-adjusted for unpaid leaves"
                          />
                        </Grid>
                      </Grid>

                      {/* ── Summary Cards ── */}
                      {calc && (
                        <>
                          <Grid container spacing={1.5} mb={3}>
                            <Grid item xs={6} sm={3}>
                              <StatCard label="Base Salary" value={fmt(selectedStaff.hrDetails?.baseSalary || 0)} color="#0891B2" icon={<SalaryIcon />} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <StatCard label="Gross Pay" value={fmt(calc.grossPay)} color="#059669" icon={<EarningIcon />} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <StatCard label="Total Deductions" value={fmt(calc.totalDeductions)} color="#DC2626" icon={<DeductionIcon />} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <StatCard label="Net Payable" value={fmt(calc.netPay)} color={calc.netPay >= 0 ? "#7C3AED" : "#DC2626"} icon={<NetPayIcon />} />
                            </Grid>
                          </Grid>

                          {/* ── Earnings Breakdown ── */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha('#059669', 0.2)}`, borderRadius: 2, background: alpha('#059669', 0.03) }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                  <Typography variant="subtitle2" fontWeight={700} color="#059669">
                                    Earnings
                                  </Typography>
                                  <IconButton size="small" color="success" onClick={addAllowance} sx={{ bgcolor: alpha('#059669', 0.1) }}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>

                                <Box display="flex" flexDirection="column" gap={0.75}>
                                  {/* Standard rows */}
                                  {[
                                    { label: `Basic (50% of gross)`, value: calc.basicPay },
                                    { label: `HRA (50% of basic)`, value: calc.hraPay },
                                    { label: `Other Allowances`, value: calc.otherAllowances },
                                  ].map(r => (
                                    <Box key={r.label} display="flex" justifyContent="space-between">
                                      <Typography variant="caption" color="text.secondary">{r.label}</Typography>
                                      <Typography variant="caption" fontWeight={600}>{fmt(r.value)}</Typography>
                                    </Box>
                                  ))}

                                  {/* Custom allowances */}
                                  {allowances.length > 0 && <Divider sx={{ my: 0.5 }} />}
                                  {allowances.map(a => (
                                    <LineItemRow
                                      key={a.id} item={a}
                                      onUpdate={updateLineItem(allowances, setAllowances)}
                                      onRemove={removeLineItem(allowances, setAllowances)}
                                      color="#059669"
                                    />
                                  ))}

                                  <Divider sx={{ my: 0.5 }} />
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" fontWeight={700}>Total Earnings</Typography>
                                    <Typography variant="caption" fontWeight={800} color="#059669">{fmt(calc.grossPay + calc.totalAllowances)}</Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>

                            {/* ── Deductions ── */}
                            <Grid item xs={12} sm={6}>
                              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha('#DC2626', 0.2)}`, borderRadius: 2, background: alpha('#DC2626', 0.03) }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                  <Typography variant="subtitle2" fontWeight={700} color="#DC2626">
                                    Deductions
                                  </Typography>
                                  <IconButton size="small" onClick={addDeduction} sx={{ bgcolor: alpha('#DC2626', 0.1), color: '#DC2626' }}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>

                                <Box display="flex" flexDirection="column" gap={0.75}>
                                  {deductions.length === 0 ? (
                                    <Typography variant="caption" color="text.disabled" textAlign="center" py={1}>
                                      Click + to add TDS, PF, or other deductions
                                    </Typography>
                                  ) : (
                                    deductions.map(d => (
                                      <LineItemRow
                                        key={d.id} item={d}
                                        onUpdate={updateLineItem(deductions, setDeductions)}
                                        onRemove={removeLineItem(deductions, setDeductions)}
                                        color="#DC2626"
                                      />
                                    ))
                                  )}
                                  <Divider sx={{ my: 0.5 }} />
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" fontWeight={700}>Total Deductions</Typography>
                                    <Typography variant="caption" fontWeight={800} color="#DC2626">{fmt(calc.totalDeductions)}</Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          </Grid>

                          {/* ── Net Pay Final Banner ── */}
                          <Paper
                            elevation={0}
                            sx={{
                              mt: 2.5, p: 2.5,
                              background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                              borderRadius: 2, color: 'white',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}
                          >
                            <Box>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Net Payable — {MONTHS[month - 1]} {year} ({daysWorked}/{workingDays} days)
                              </Typography>
                              <Typography variant="h4" fontWeight={900}>{fmt(calc.netPay)}</Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                              <Button
                                variant="outlined"
                                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                onClick={handleDownloadPDF}
                              >
                                Download Slip
                              </Button>
                              <Button
                                variant="contained"
                                disabled={markingPaid || calc.netPay <= 0}
                                sx={{ bgcolor: 'white', color: '#7C3AED', fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                                onClick={handleMarkAsPaid}
                              >
                                {markingPaid ? 'Processing...' : '✅ Mark as Paid'}
                              </Button>
                            </Box>
                          </Paper>
                        </>
                      )}
                    </Box>
                  </Paper>
                </motion.div>
              )}
            </AnimatePresence>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </AnimatedPage>
  );
};

export default Payroll;